import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useQuery } from 'react-apollo';

import { formatPriceForDisplay } from '../../../../../util/format-money';
import BoxedItem from '../../../../../components/global/BoxedItem';
import Button from '../../../../../components/global/Button';
import Badge from '../../../../../components/global/Badge';
import CheckmarkIcon from '../../../../../components/icons/CheckmarkIcon';
import { EVENT_ITEM_PLAN } from '../../../../../constants/event-items';
import { checkoutPrice as checkoutPriceQuery } from '../../../../../graphql/queries/checkout';
import useAuthenticated from '../../../../../hooks/useAuthenticated';

const propTypes = {
  className: PropTypes.string,
  eventItem: PropTypes.shape({
    id: PropTypes.string,
    video_guestbook_enabled: PropTypes.bool,
  }),
  features: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  ),
  disabledFeatures: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  ),
  title: PropTypes.string,
  processing: PropTypes.bool,
  onCheckout: PropTypes.func.isRequired,
  skipPayment: PropTypes.bool,
  badgeText: PropTypes.node,
  checkoutBtnProps: PropTypes.object,
  albumFolderId: PropTypes.string,
};

const WeddingEventItem = ({
  eventItem,
  title,
  features,
  disabledFeatures = [],
  className,
  onCheckout,
  processing,
  skipPayment = false,
  badgeText,
  checkoutBtnProps = {},
  checkoutBtnText,
  albumFolderId,
}) => {
  const authenticated = useAuthenticated();
  const [displayMobileFeatures, setDisplayMobileFeatures] = useState(false);
  const classes = classNames({
    'wedding-event-features': true,
    'wedding-event-features-list-open': displayMobileFeatures,
    [className]: !!className,
  });

  const isFree =
    eventItem?.price?.value === 0 || eventItem?.plan === EVENT_ITEM_PLAN.FREE;

  const { data: { checkoutPrice } = {}, loading: loadingPricing } = useQuery(
    checkoutPriceQuery,
    {
      variables: {
        event_items: [
          {
            event_item_id: eventItem?.id,
            quantity: 1,
            album_folder_id: albumFolderId,
          },
        ],
      },
      skip: !authenticated || !eventItem || isFree,
      fetchPolicy: 'network-only',
    },
  );

  const checkoutButtonLabel = (() => {
    if (checkoutBtnText) {
      return checkoutBtnText;
    }

    if (loadingPricing) {
      return 'Loading price...';
    }

    if (!authenticated) {
      // Fallback to whatever pricing this item  has
      return eventItem?.price?.value === 0
        ? 'FREE'
        : `$${formatPriceForDisplay(eventItem?.price?.value)}`;
    }

    return isFree
      ? 'FREE'
      : `$${formatPriceForDisplay(checkoutPrice?.total_price?.value)}`;
  })();

  return (
    <BoxedItem className={classes}>
      <div className="wedding-event-features-header">
        {badgeText && (
          <Badge badgeStyle="yellow" badgeSize="lg">
            {badgeText}
          </Badge>
        )}
        <p className="wedding-event-features-title">{title}</p>

        <Button
          onClick={() => {
            onCheckout({ eventItem, skipPayment });
          }}
          btnBlock
          btnSize="lg"
          disabled={processing || loadingPricing}
          {...checkoutBtnProps}
        >
          <Fragment>{checkoutButtonLabel}</Fragment>
        </Button>
      </div>

      <div className="wedding-event-features-content">
        <ul>
          {features.map((feature, index) => (
            <li key={index} className="text-dark text-bold">
              {typeof feature === 'string' ? (
                <>
                  <CheckmarkIcon
                    color="#f1794e"
                    size="14px"
                    className="feature-icon"
                  />
                  <span>{feature}</span>
                </>
              ) : (
                <span>{feature}</span>
              )}
            </li>
          ))}
          {disabledFeatures.map((feature, index) => (
            <li key={index} className="text-faded text-bold">
              <span className="feature-icon">&times;</span>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="wedding-event-features-footer d-md-none">
        <Button
          btnStyle="reset"
          onClick={() => setDisplayMobileFeatures(!displayMobileFeatures)}
          className="text-extra-bold"
          btnBlock
        >
          {displayMobileFeatures ? 'Hide' : 'View'} features
        </Button>
      </div>
    </BoxedItem>
  );
};

WeddingEventItem.propTypes = propTypes;

export default WeddingEventItem;
